import type { LucideIcon } from 'lucide-react';

/** Study tool registry types (split out of studyTools.ts, SIR-HYG-07). */

export type StudyToolTone = 'sky' | 'coral' | 'mint' | 'sunshine';

/**
 * A topic group inside one subject, so tools are found by what they teach
 * rather than scrolled through as one long list.
 */
export interface StudyToolCategory {
  id: string;
  subjectKey: string;
  name: string;
  description: string;
  icon: LucideIcon;
}

export interface StudyTool {
  id: string;
  subjectKey: string;
  category: string;
  name: string;
  description: string;
  path: string;
  actionLabel: string;
  icon: LucideIcon;
  tone: StudyToolTone;
  /**
   * First Year whose directory recommends the tool; missing means Year 6. A discovery starting
   * point only, never a curriculum alignment claim. Chemistry values follow the product bands in
   * curriculum `school-study/chemistry-tool-year-matrix.json` (study-chemistry-tools-prd.md §3).
   */
  availableFrom?: number;
  /**
   * General tool (platform PRD D-STUDY-GENERAL-TOOLS-20261007): a reference tool shown in every Year,
   * in its own "General tools" row. `availableFrom` stays its first content Year, not a visibility limit.
   */
  general?: boolean;
  /** Directory mode; access and favourites keep the parent tool identity. */
  view?: string;
  keywords?: string;
  yearLabel?: string;
  toolKey?: string;
  toolVersion?: `${number}.${number}.${number}`;
  curriculumPolicy?: 'reviewed-binding-required';
}

/** First Year whose directory shows the tool: Year 6 for a general tool, else its start Year. */
export function toolVisibleFromYear(tool: StudyTool): number {
  return tool.general ? 6 : (tool.availableFrom ?? 6);
}
